import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Chip, Badge, Typography, Grid, Paper, IconButton, Tooltip } from '@mui/material';
import { DialogTitle, DialogContent, Box } from '@mui/material';
import { Masonry } from '@mui/lab';
import { AssetIcon } from '../Icons';
import MiniField from '../../Global/Fields/MiniField';
import { User, HtmlContainer, UserGroup } from '../../Global/Components';
import { blue, red, green, deepPurple } from '@mui/material/colors';
import { EyeIcon } from '../Icons';
import axios from 'axios';
import RoundedDialog from './RoundedDialog';
import StandardDialogActions from './StandardDialogActions';
import StatusButton from '../../Hubs/asset/components/StatusButton';
import { isEmpty } from 'lodash';

const DefaultStatuses = [
  { _id: 'ok', title: 'OK', color: green[600] },
  { _id: 'lost', title: 'Lost', color: red[600] },
  { _id: 'quarantined', title: 'Quarantined', color: red[600] },
  { _id: 'expired', title: 'Expired', color: red[600] },
  { _id: 'overdue', title: 'Overdue', color: red[600] },
];

export default function AssetChip({ assetId, withoutFields = false, hasStatusButton, canEditStatus }) {
  const [assetInfo, setAssetInfo] = useState();
  const [assetDialog, setAssetDialog] = useState(false);
  const [loading, setLoading] = useState();
  const [customStatuses, setCustomStatuses] = useState([]);
  const loadStatuses = isEmpty(customStatuses);
  const { user } = useSelector(({ profile }) => profile);
  const isPortalUser = user?.access === 'portal';
  useEffect(() => {
    if (!loadStatuses) return;
    setLoading(true);
    axios
      .get('/asset/status/getStatuses')
      .then((res) => {
        setCustomStatuses(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [loadStatuses]);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    axios
      .get('/asset/getAssetInfo', { params: { assetId, chipData: true } })
      .then((res) => {
        setAssetInfo(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [assetId]);

  /* eslint-disable */
  return (
    <>
      <Chip
        variant="outlined"
        label={
          loading ? (
            '...loading'
          ) : (
            <Grid container item direction="row">
              <Grid
                container
                item
                direction={'row'}
                justifyContent={'space-between'}
                wrap={'nowrap'}
                sx={{ heigh: '100%', alignItems: 'center', minWidth: '100%' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography variant="body2" fontWeight="bold" width="100%">
                    {`${assetInfo?.prefix ? assetInfo.prefix + ' ' : ''}${assetInfo?.title}`}
                  </Typography>
                </div>
                <Grid container item justifyContent="flex-end" alignItems={'center'} direction={'row'}>
                  <Tooltip title="Show more info">
                    <IconButton
                      sx={{ ml: 1, height: 'fit-content', alignSelf: 'center' }}
                      onClick={() => setAssetDialog(true)}
                    >
                      <EyeIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Go to asset">
                    <IconButton
                      sx={{ ml: 1, height: 'fit-content', alignSelf: 'center' }}
                      onClick={() =>
                        window.open(isPortalUser ? `/portal/asset/${assetId}` : `/asset/${assetId}`, '_blank')
                      }
                    >
                      <AssetIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                  </Tooltip>
                  {hasStatusButton && (
                    <StatusButton
                      assetId={assetInfo?._id}
                      status={[...DefaultStatuses, ...customStatuses].find((stat) => stat._id === assetInfo?.statusId)}
                      onChange={(update) => {
                        setAssetInfo({ ...assetInfo, statusId: update?.status?._id });
                      }}
                      disabled={!canEditStatus}
                      additionalStyles={{
                        fontSize: 10,
                        height: 'fit-content',
                        marginRight: 5,
                        marginLeft: 5,
                      }}
                    />
                  )}
                </Grid>
              </Grid>
              {!withoutFields &&
                assetInfo?.fields?.slice(0, 3).map((field) => (
                  <Grid item key={field.id} sx={{ m: 0.5, ml: 0.5 }}>
                    <Grid container component={Paper} variant="outlined" sx={{ m: 0.1, ml: 0.25, py: 1, px: 1.5 }}>
                      <MiniField field={field} initial={field.filledValue} />
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          )
        }
        sx={{
          height: 'auto',
          width: '90%',
          justifyContent: 'flex-start',
          '& .MuiChip-label': {
            display: 'flex',
            padding: '8px',
            ml: '5px',
            width: '100%',
          },
          padding: '4px',
        }}
      />
      <AssetDialog open={assetDialog} onClose={() => setAssetDialog(false)} assetId={assetId} />
    </>
  );
  /* eslint-enable */
}

function AssetDialog({ open, onClose, assetId, mobile = false, largeDevices = true }) {
  const [asset, setAsset] = useState();
  const [loading, setLoading] = useState(false);

  const assetExpired = false;
  const assetRecalled = false;
  const bookingUnavailable = false;
  const id = assetId;

  useEffect(() => {
    setLoading(true);
    axios
      .get('/asset/getAsset', { params: { id } })
      .then(({ data }) => {
        setAsset(data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
    //eslint-disable-next-line
  }, [open]);
  return (
    <RoundedDialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Brief Asset Info</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2} mt={1}>
          <Grid container item alignItems="center" spacing={1} sx={{ pb: 5 }}>
            <Grid
              container
              item
              component={Paper}
              variant="outlined"
              alignItems="center"
              wrap="nowrap"
              sx={{ minHeight: 50, p: 1, boxShadow: `1px 1px 10px 1px ${asset?.status?.color || 'transparent'}` }}
            >
              <Grid container item width="100%" wrap="nowrap" alignItems="center" direction="column">
                <Grid container item width="100%" direction="row" wrap="nowrap" sx={{ flex: 1 }}>
                  <Grid
                    container
                    item
                    alignItems="center"
                    wrap="nowrap"
                    justifyContent={mobile ? 'space-between' : 'flex-start'}
                    sx={{ p: 0 }}
                  >
                    <Typography variant={largeDevices ? 'h4' : 'body2'} sx={{ ml: 2, width: '100%' }} flex={1} noWrap>
                      {asset?.prefix ? (
                        <span style={{ color: blue[500] }}>
                          {asset?.prefix}-{asset?.number}{' '}
                        </span>
                      ) : (
                        <span style={{ color: blue[500] }}>{asset?.number} </span>
                      )}
                      {asset?.title}
                    </Typography>
                  </Grid>
                  <Grid container item alignItems="center" justifyContent="flex-end" flex={0} wrap="nowrap" gap={1.5}>
                    {!mobile && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {largeDevices && (
                          <Typography color="text.secondary" sx={{ fontSize: 14, mr: 1, ml: 0.5 }}>
                            Owner
                          </Typography>
                        )}
                        <User onlyAvatar={!largeDevices} id={asset?.ownerId} avatarSize={25} />
                      </Box>
                    )}
                    {asset?.number && largeDevices && (
                      <Chip
                        size="small"
                        sx={{ color: 'white', background: deepPurple[500] }}
                        label={`#${asset?.number}`}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item alignItems="center" spacing={1}>
            {asset?.currBooking && (
              <Grid container item>
                <Grid
                  container
                  component={Paper}
                  variant="outlined"
                  alignItems="center"
                  sx={{
                    p: 1,
                    border: assetExpired || assetRecalled ? `2px solid ${red[600]}` : '',
                  }}
                >
                  {(assetExpired || assetRecalled) && (
                    <Grid container item alignItems="center">
                      <Typography variant="h6" sx={{ color: red[600] }}>
                        Please return the asset - {assetExpired ? 'it has expired' : 'it has been recalled'}
                      </Typography>
                    </Grid>
                  )}
                  <Grid container item alignItems="center">
                    <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 500 }}>
                      This asset is currently booked out by
                    </Typography>
                    <UserGroup avatarSize={32} ids={asset?.currBooking.bookedFor} />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid container item wrap="nowrap" alignItems="flex-start" direction={largeDevices ? 'row' : 'column'}>
            {asset?.coverPicture && (
              <Grid container item direction="column" width="fit-content">
                <Grid
                  container
                  item
                  component={Paper}
                  variant="outlined"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    p: 1,
                    width: 225,
                    height: 225,
                    mt: 2,
                    mr: largeDevices ? 2 : 0,
                    mb: 2,
                  }}
                >
                  <Grid
                    container
                    item
                    sx={{
                      height: '100%',
                      width: '100%',
                      margin: '0 auto',
                      backgroundImage: `url(${asset?.coverPicture.location})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <Grid
              container
              item
              component={Paper}
              variant="outlined"
              alignContent="flex-start"
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'nowrap',
                gap: '5px',
                mt: 2,
                p: 1,
              }}
            >
              <Grid container item direction="row" justifyContent="space-between" wrap="wrap" gap={0.5}>
                <Grid container item lg={3.9} md={6} xs={12} alignItems={'center'} gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Overall status
                  </Typography>
                  <Typography variant="body1">{asset?.status?.title || '-'}</Typography>
                </Grid>
                {asset?.bookingExpiresAt && asset?.currBooking && !assetExpired && (
                  <Grid container item lg={3.9} md={6} xs={12} alignItems={'center'} gap={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Asset expires in
                    </Typography>
                    <Typography variant="body1">{asset?.bookingExpiresAt}</Typography>
                  </Grid>
                )}
                {asset?.managementStatus && (
                  <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Management status
                    </Typography>
                    <Chip
                      label={asset?.managementStatus.title}
                      sx={{
                        background: asset?.managementStatus.color,
                        color: (theme) => theme.palette.getContrastText(asset?.managementStatus.color),
                      }}
                    />
                  </Grid>
                )}
                <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Booking status
                  </Typography>
                  <Chip
                    label={bookingUnavailable ? 'Unavailable' : asset?.currBooking ? 'In use' : 'Available'}
                    sx={{
                      color: 'white',
                      background: asset?.currBooking || bookingUnavailable ? red[600] : green[600],
                    }}
                  />
                </Grid>
                <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {asset?.assetlocation?.text || asset?.assetlocation?.title || '-'}
                  </Typography>
                </Grid>
                <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Group
                  </Typography>
                  <Typography variant="body1">{asset?.assetgroup?.text || asset?.assetgroup?.title || '-'}</Typography>
                </Grid>
                {['jobs', 'either'].includes(asset?.assetprofile?.expirySettings?.expiryType) && (
                  <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Jobs used
                    </Typography>
                    <Typography
                      sx={{
                        color: asset?.beenUsedForJobs > asset?.assetprofile?.expirySettings?.jobsCount ? red[600] : '',
                        fontWeight: asset?.beenUsedForJobs > asset?.assetprofile?.expirySettings?.jobsCount ? 500 : 400,
                      }}
                    >
                      {asset?.beenUsedForJobs || 0}/{asset?.assetprofile?.expirySettings?.jobsCount}
                    </Typography>
                  </Grid>
                )}
                {['time', 'either'].includes(asset?.assetprofile?.expirySettings?.expiryType) &&
                  !asset?.currBooking &&
                  !assetExpired && (
                    <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Remaining time
                      </Typography>
                      <Typography sx={{ color: asset?.remainingMs < 0 ? red[600] : '' }}>
                        {asset?.remainingMs < 0 && '-'}
                        {asset?.remainingMs || 0}
                      </Typography>
                    </Grid>
                  )}
                {asset?.expiryDate && (
                  <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Expiry date
                    </Typography>
                    <Typography>{asset?.expiryDate}</Typography>
                  </Grid>
                )}
                <Grid container item lg={3.9} md={6} xs={12} height="50px" alignItems={'center'} gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Profile
                  </Typography>
                  <Typography variant="body1" style={{ color: blue[800] }}>
                    {asset?.assetprofile?.title || '-'}
                  </Typography>
                </Grid>
              </Grid>
              {asset?.fields?.length > 0 && Array.isArray(asset?.fields) && (
                <Grid container item alignContent="flex-start" sx={{ mt: 2, width: '100%' }}>
                  <Masonry columns={{ lg: 3, md: 3, xs: 1 }} spacing={1}>
                    {asset?.fields.slice(0, 3).map((field) => (
                      <Grid item key={field.id}>
                        <Grid container component={Paper} variant="outlined" sx={{ py: 1, px: 1.5 }}>
                          <MiniField field={field} initial={field.filledValue} />
                        </Grid>
                      </Grid>
                    ))}
                  </Masonry>
                </Grid>
              )}
            </Grid>
          </Grid>

          {asset?.description && (
            <Grid container item>
              <Grid container item>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
              </Grid>
              <Grid container item maxWidth="100%">
                <HtmlContainer html={asset?.description} style={{ width: '100%' }} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={() => onClose(false)} />
    </RoundedDialog>
  );
}
